"use client";

import { useState, useEffect, useRef, memo, useMemo } from "react";
import Image from "next/image";

// Memoized Venture Investor Card
const VentureCard = memo(function VentureCard({ venture }) {
  return (
    <div className="border bg-white border-black rounded-lg flex items-center justify-center md:h-[100px] h-[80px] w-full">
      <Image
        src={venture.logo}
        alt={venture.name}
        width={120}
        height={45}
        className="md:w-[160px] md:h-auto w-[120px]"
        style={{ objectFit: "contain" }}
        loading="lazy"
        sizes="(max-width: 768px) 120px, 160px"
      />
    </div>
  );
});

// Memoized Angel Investor Card
const AngelCard = memo(function AngelCard({ investor, isChanging }) {
  return (
    <div className="relative mb-3">
      <div
        className={`w-[80px] h-[80px] relative mx-auto transition-opacity duration-300 ${
          isChanging ? "opacity-0" : "opacity-100"
        }`}
      >
        <Image
          src={investor.image}
          alt={investor.name}
          fill
          sizes="80px"
          className="rounded-lg object-cover"
          loading="lazy"
        />
      </div>
    </div>
  );
});

export default function Investors({ investors }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [changingIndex, setChangingIndex] = useState(-1);
  const timerRef = useRef(null);

  // Memoize filtered investors to prevent recalculation
  const { ventureInvestors, angelInvestors, totalPages } = useMemo(() => {
    const venture = investors.filter((investor) => investor.type === "venture");
    const angel = investors.filter((investor) => investor.type === "angel");
    const investorsPerPage = 12;
    const pages = Math.ceil(angel.length / investorsPerPage);
    
    return {
      ventureInvestors: venture,
      angelInvestors: angel,
      totalPages: pages
    };
  }, [investors]);

  // Memoize current page investors
  const currentInvestors = useMemo(() => {
    const investorsPerPage = 12;
    const startIndex = (currentPage % totalPages) * investorsPerPage;
    return angelInvestors.slice(startIndex, startIndex + investorsPerPage);
  }, [angelInvestors, currentPage, totalPages]);

  // Split current investors for easier rendering
  const { firstRowInvestors, secondRowInvestors } = useMemo(() => {
    return {
      firstRowInvestors: currentInvestors.slice(0, 6),
      secondRowInvestors: currentInvestors.slice(6, 12)
    };
  }, [currentInvestors]);

  useEffect(() => {
    // Only run animation if we have multiple pages
    if (totalPages <= 1) return;

    const changeSequentially = () => {
      clearTimeout(timerRef.current);
      setChangingIndex(0);

      let currentIndex = 0;
      const changeNext = () => {
        currentIndex++;

        if (currentIndex >= 12) {
          setChangingIndex(-1);
          setCurrentPage((prevPage) => (prevPage + 1) % totalPages);
          return;
        }

        setChangingIndex(currentIndex);
        timerRef.current = setTimeout(changeNext, 150);
      };

      timerRef.current = setTimeout(changeNext, 300);
    };

    const interval = setInterval(changeSequentially, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timerRef.current);
    };
  }, [totalPages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <section
      className="w-full py-16"
      style={{ backgroundColor: "rgb(255, 250, 241)" }}
      id="investors"
    >
      <div className="w-[90%] md:w-[80%] mx-auto">
        {/* Header Section */}
        <div className="mb-10">
          <p className="text-sm md:text-base uppercase font-medium tracking-wide text-gray-600 mb-4">
            BEHIND OUR GROWTH
          </p>
          <h2 className="text-3xl md:text-[36px] font-medium mb-6">
            Backed by the best
          </h2>
          <p className="text-gray-700 text-sm md:text-base font-normal">
            With the trust of 50+ Angel Investors, we&apos;re building futures
            together
          </p>
        </div>

        {/* Main Content - Split into left and right sections with proper spacing */}
        <div className="flex flex-col md:flex-row md:items-start md:space-x-0">
          {/* Left Section - Venture Capital Logos */}
          {/* <div className="md:w-[240px] flex md:flex-col gap-6">
            {ventureInvestors.map((venture) => (
              <VentureCard key={venture.id} venture={venture} />
            ))}
          </div> */}

          {/* Vertical divider with exact spacing
          <div className="hidden md:block w-px bg-black mx-24 self-stretch"></div> */}

          {/* Right Section - Grid of Angel Investors */}
          <div className="flex-1 mt-6 md:mt-0">
            <div className="border-t border-gray-200 pt-6 md:border-0 md:pt-0">
              <div className="grid grid-cols-3 md:grid-cols-8 gap-3">
                {/* First row */}
                {firstRowInvestors.map((investor, index) => (
                  <AngelCard
                    key={`top-${investor.id}-${currentPage}`}
                    investor={investor}
                    isChanging={changingIndex === index}
                  />
                ))}

                {/* Second row */}
                {secondRowInvestors.map((investor, index) => (
                  <AngelCard
                    key={`bottom-${investor.id}-${currentPage}`}
                    investor={investor}
                    isChanging={changingIndex === index + 6}
                  />
                ))}
              </div>

              {/* "and more..." text only visible on mobile */}
              {totalPages > 1 && (
                <div className="text-right mt-4 text-gray-600 italic md:hidden">
                  and more...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}